import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { EntityProvider } from './contexts/EntityContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';
import Proposals from './pages/Proposals';
import ProposalCreate from './pages/ProposalCreate';
import ProposalEdit from './pages/ProposalEdit';
import Inventory from './pages/Inventory';
import Services from './pages/Services';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import Users from './pages/Users';
import SettingsMenu from './pages/SettingsMenu';
import SettingsLanguage from './pages/SettingsLanguage';
import SettingsSections from './pages/SettingsSections';
import SettingsTemplates from './pages/SettingsTemplates';
import SettingsFormulaRules from './pages/SettingsFormulaRules';
import SettingsGeneral from './pages/SettingsGeneral';
import SettingsPDF from './pages/SettingsPDF';
import SettingsServiceCategories from './pages/SettingsServiceCategories';
import SettingsEntities from './pages/SettingsEntities';
import Suppliers from './pages/Suppliers';
import ProjectManagement from './pages/ProjectManagement';
import Clients from './pages/Clients';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Check if we need to redirect after entity change (only once on mount)
  useEffect(() => {
    const redirectData = localStorage.getItem('entityChangeRedirect');
    if (redirectData) {
      try {
        const { path, timestamp } = JSON.parse(redirectData);
        // Only redirect if timestamp is recent (within 2 seconds) to prevent issues on manual refresh
        const timeDiff = Date.now() - timestamp;
        if (timeDiff < 2000 && path && path !== '/dashboard' && path !== '/') {
          localStorage.removeItem('entityChangeRedirect');
          const redirectPathOnly = path.split('?')[0];
          const currentPath = location.pathname;
          if (currentPath !== redirectPathOnly) {
            navigate(path, { replace: true });
            setIsInitialMount(false);
            return;
          }
        } else {
          // Flag is too old or invalid, remove it
          localStorage.removeItem('entityChangeRedirect');
        }
      } catch (e) {
        // Invalid data, remove it
        localStorage.removeItem('entityChangeRedirect');
      }
    }
    
    // On initial mount, check if we should redirect from dashboard to last visited path
    if (isInitialMount && location.pathname === '/dashboard') {
      const lastPath = localStorage.getItem('lastVisitedPath');
      if (lastPath && lastPath !== '/dashboard' && lastPath !== '/') {
        navigate(lastPath, { replace: true });
      }
    }
    
    setIsInitialMount(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Remember last visited path (exclude login and dashboard) to restore after refresh
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      // Clear lastVisitedPath when navigating to dashboard
      localStorage.removeItem('lastVisitedPath');
    } else if (location.pathname !== '/login') {
      localStorage.setItem('lastVisitedPath', location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/proposals" element={<Proposals />} />
      <Route path="/projects" element={<ProjectManagement />} />
      <Route path="/proposals/new" element={<ProposalCreate />} />
      <Route path="/proposals/:id/edit" element={<ProposalEdit />} />
      <Route path="/inventory" element={<Inventory />} />
      <Route path="/services" element={<Services />} />
      <Route path="/invoices" element={<Invoices />} />
      <Route path="/invoices/:id" element={<InvoiceDetail />} />
      <Route path="/suppliers" element={<Suppliers />} />
      <Route path="/users" element={<Navigate to="/settings/users" replace />} />
      <Route path="/settings/users" element={<Users />} />
      <Route path="/clients" element={<Clients />} />
      <Route path="/settings" element={<SettingsMenu />} />
      <Route path="/settings/language" element={<SettingsLanguage />} />
      <Route path="/settings/sections" element={<SettingsSections />} />
      <Route path="/settings/templates" element={<SettingsTemplates />} />
      <Route path="/settings/rules" element={<SettingsFormulaRules />} />
      <Route path="/settings/general" element={<SettingsGeneral />} />
      <Route path="/settings/pdf" element={<SettingsPDF />} />
      <Route path="/settings/service-categories" element={<SettingsServiceCategories />} />
      <Route path="/settings/entities" element={<SettingsEntities />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <LanguageProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <CurrencyProvider>
        <EntityProvider>
          <NavigationProvider>
            <Layout>
              <AppContent />
            </Layout>
          </NavigationProvider>
        </EntityProvider>
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;
